import { Box, Skeleton } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { useAsyncFetch } from '@tom-ui/utils'
import _ from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { ContainerDetailsCard } from './ContainerDetailsCard'

interface Props {
  journeyId: number
  highlight?: boolean
  isReserved?: boolean
  schemeBackgroundColor?: string
}
export const ContainerDetailsCardByJourneyId: FC<Props> = observer(props => {
  const { journeyId, highlight, isReserved, schemeBackgroundColor } = props
  const { containerJourneyItemStore } = usePlanningStore()

  useAsyncFetch(
    async () => await containerJourneyItemStore.fetchById(journeyId),
    [containerJourneyItemStore, journeyId],
  )

  const containerJourney = computed(() =>
    _.values(containerJourneyItemStore.elements).find(journey => journey.id === journeyId),
  ).get()

  const orderItem = computed(() => {
    if (!containerJourney) return undefined

    if (
      containerJourney.inboundOrder?.data.status === 'Fulfilled' &&
      containerJourney.outboundOrder?.data.containerNumber
    )
      return containerJourney.outboundOrder

    return containerJourney?.inboundOrder
  }).get()

  const orderData = computed(() => orderItem?.data).get()

  if (!orderData || !containerJourney) {
    // Loading footprint must match the loaded card's minHeight (145) so cells
    // whose parent has no explicit height (e.g. the flexible-block list) reserve
    // visible space — otherwise `height: 100%` collapses to 0 and the cell
    // disappears, causing tooltip-count vs visible-cell-count mismatches.
    //
    // Wrap Skeleton in a colored Box so the loading shimmer/pulse stays
    // visible while the scheme color shows through the semi-transparent
    // skeleton overlay. Reverts to plain MUI Skeleton when no scheme color.
    if (!schemeBackgroundColor) {
      return (
        <Skeleton
          variant='rectangular'
          width='100%'
          height='100%'
          sx={{ minHeight: 145 }}
        />
      )
    }
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 145,
          bgcolor: schemeBackgroundColor,
        }}
      >
        <Skeleton variant='rectangular' width='100%' height='100%' sx={{ opacity: 0.55 }} />
      </Box>
    )
  }

  const rawCategory = containerJourney.data.category ?? orderItem?.journeyCategory
  const category = rawCategory && rawCategory !== 'None' ? rawCategory : undefined

  return (
    <ContainerDetailsCard
      id={orderData.id}
      number={orderData.containerNumber ?? ''}
      isoCode={orderData.containerIsoCode ?? ''}
      size={orderData.containerLength ?? 20}
      weight={orderData.grossWeight ?? 0}
      status={containerJourney.status ?? 'Expected'}
      pod={orderData.portOfDischarge}
      pol={orderData.portOfLoading}
      direction={orderData.direction}
      shippingLine={orderItem?.container?.data.operator}
      archetype={orderData.isoCodeArchetype}
      weightClass={orderItem?.weightClass?.name}
      category={category}
      finalDestination={orderData.finalDestination}
      inboundVisit={containerJourney.inboundOrder?.visit?.data.identifier}
      outboundVisit={containerJourney.outboundOrder?.visit?.data.identifier}
      isEmpty={orderData.isEmpty}
      isOog={orderData.isOOG}
      isDangerous={orderData.isDangerous || (orderData.imoClasses?.length ?? 0) > 0}
      isReefer={!!containerJourney.data.coolingOrderId}
      isDamaged={orderData.isDamaged}
      hasHolds={orderItem?.hasActiveHold}
      orderData={orderData}
      highlight={highlight}
      isReserved={isReserved}
      schemeBackgroundColor={schemeBackgroundColor}
    />
  )
})
